export const WASH_SOURCE_COLORS = {
  Borehole: "#1f77b4",
  "Shallow Well": "#2ca02c",
  Berkad: "#ff7f0e",
  "Haffir/Earth Dam": "#d62728",
  "Spring protection": "#9467bd",
  "River Water": "#8c564b",
  Spring: "#e377c2",
  "Piped Network": "#17becf",
  "Rainwater Harvesting": "#bcbd22",
  "Water Trucking": "#7f7f7f",
  Other: "#aec7e8",
};

const PALETTE = [
  "#0b5a8c",
  "#1a8fbf",
  "#2bb0a6",
  "#f0a202",
  "#e4572e",
  "#6c63ff",
  "#2e7d32",
  "#8e44ad",
  "#16a085",
  "#c0392b",
  "#2980b9",
  "#d35400",
];

export const colorForKey = (key, index = 0) => {
  if (WASH_SOURCE_COLORS[key]) return WASH_SOURCE_COLORS[key];
  return PALETTE[index % PALETTE.length];
};

export const isFunctional = (value) => {
  const v = String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  if (!v) return false;
  if (v.includes("non") || v.includes("partial") || v.includes("not ")) return false;
  // Matches "Functional" and common API typo "Funtional"
  return (
    v.includes("functional") ||
    v.includes("funtional") ||
    v === "funct" ||
    v.startsWith("fun")
  );
};

export const formatCompactNumber = (value) => {
  const n = Number(value) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2).replace(/\.00$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1).replace(/\.0$/, "")}K`;
  return n.toLocaleString();
};

export const formatUsdCompact = (value) => {
  const n = Number(value) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2).replace(/\.00$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const uniqueSorted = (values) =>
  [...new Set(values.filter((v) => v !== null && v !== undefined && String(v).trim() !== ""))]
    .map(String)
    .sort((a, b) => a.localeCompare(b));

export const buildFilterOptions = (rows) => ({
  regions: uniqueSorted(rows.map((r) => r.region)),
  districts: uniqueSorted(rows.map((r) => r.district)),
  years: uniqueSorted(rows.map((r) => r.year_established)),
  donors: uniqueSorted(rows.map((r) => r.donor)),
  projects: uniqueSorted(rows.map((r) => r.project)),
  waterSources: uniqueSorted(rows.map((r) => r.water_source)),
  workTypes: uniqueSorted(rows.map((r) => r.work_type)),
});

export const applyWashFilters = (rows, filters) =>
  (rows || []).filter((row) => {
    if (filters.region && (row.region || "Unknown") !== filters.region) return false;
    if (filters.district && (row.district || "Unknown") !== filters.district) return false;
    if (filters.year && String(row.year_established) !== String(filters.year)) return false;
    if (filters.donor && row.donor !== filters.donor) return false;
    if (filters.project && row.project !== filters.project) return false;
    if (filters.water_source && (row.water_source || "Unknown") !== filters.water_source)
      return false;
    if (filters.work_type && row.work_type !== filters.work_type) return false;
    if (filters.water_quality && (row.water_quality || "Unknown") !== filters.water_quality)
      return false;
    if (filters.program_area && (row.program_area || "Unknown") !== filters.program_area)
      return false;
    return true;
  });

const countBy = (rows, keyFn) => {
  const map = new Map();
  rows.forEach((row) => {
    const key = keyFn(row);
    if (!key) return;
    map.set(key, (map.get(key) || 0) + 1);
  });
  return [...map.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
};

const sumBy = (rows, groupFn, valueFn) => {
  const map = new Map();
  rows.forEach((row) => {
    const key = groupFn(row);
    if (!key) return;
    map.set(key, (map.get(key) || 0) + (Number(valueFn(row)) || 0));
  });
  return [...map.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
};

export const computeWashDashboardStats = (rows) => {
  const list = rows || [];
  const waterSources = list.length;
  const functional = list.filter((r) => isFunctional(r.functionality)).length;
  const beneficiaries = list.reduce(
    (sum, r) => sum + (Number(r.total_beneficiaries) || 0),
    0
  );
  const projects = new Set(
    list.map((r) => r.project).filter((v) => String(v || "").trim())
  ).size;
  const investment = list.reduce((sum, r) => sum + (Number(r.budget_value) || 0), 0);
  const donors = new Set(
    list.map((r) => r.donor).filter((v) => String(v || "").trim() && String(v).toUpperCase() !== "N/A")
  ).size;

  const byWaterSource = countBy(list, (r) => r.water_source || "Unknown");
  const byRegion = countBy(list, (r) => r.region || "Unknown");
  const byWaterQuality = countBy(list, (r) => r.water_quality || "Unknown");
  const beneficiariesByProgram = sumBy(
    list,
    (r) => r.program_area || "Unknown",
    (r) => r.total_beneficiaries
  );
  const byDistrict = countBy(list, (r) => r.district || "Unknown").slice(0, 4);
  const byDonor = countBy(
    list.filter((r) => r.donor && String(r.donor).toUpperCase() !== "N/A"),
    (r) => r.donor
  ).slice(0, 10);

  const mapPoints = list
    .map((r) => {
      const lat = Number(r.latitude);
      const lng = Number(r.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lng) || (lat === 0 && lng === 0)) {
        return null;
      }
      return {
        id: r.id,
        lat,
        lng,
        project: r.project,
        site_name: r.site_name,
        water_source: r.water_source || "Other",
        district: r.district,
        region: r.region,
        functionality: r.functionality,
      };
    })
    .filter(Boolean);

  return {
    kpis: {
      waterSources,
      functional,
      beneficiaries,
      projects,
      investment,
      donors,
    },
    byWaterSource,
    byRegion,
    byWaterQuality,
    beneficiariesByProgram,
    byDistrict,
    byDonor,
    mapPoints,
  };
};
